import React from "react";
import { useTranslation } from "react-i18next";
import closeIcon from "../../assets/cross.svg";

const DetailStudentModal = ({ onClose, student, onToggleStatus, onDepartStudent, canManage = true }) => {
  const { t, i18n } = useTranslation();
  const isActive = student?.status?.toUpperCase() === "ACTIVE";
  const studentName = `${student?.firstName || ""} ${student?.lastName || ""}`.trim() || student?.slug || t("manageStudents.na");
  const formattedDate = student?.createdAt
    ? new Date(student.createdAt).toLocaleDateString(i18n.resolvedLanguage || "en", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : t("manageStudents.na");

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center">
      <div className="w-[640px] bg-white rounded-[20px] overflow-hidden font-['Manrope']">
        {/* Header */}
        <div className="px-6 py-[21px] bg-gradient-to-br from-white to-blue-100 border-b border-[#DAEBFF] flex justify-between items-center">
          <h2 className="text-[18px] font-bold text-gray-900">
            {t("modals.studentDetails.title")}
          </h2>
          <div className="flex items-center gap-3">
            {/* Export functionality - commented out for future use
            <button
              onClick={onExport}
              disabled={exporting}
              className={`px-[10px] py-1.5 border border-blue-500 rounded-md flex items-center gap-1.5 transition-colors ${
                exporting ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-50"
              }`}
            >
              <img src={Export} alt="Export" className="w-4 h-4" />
              <span className="text-gray-900 text-sm font-medium">
                {exporting ? "Exporting..." : "Export"}
              </span>
            </button>
            */}
            <button onClick={onClose} className="p-0.5">
              <img src={closeIcon} alt={t("modals.common.close")} className="w-[22px] h-[22px]" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pb-5 flex flex-col gap-6 mt-5">
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-3.5">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-base font-normal">
                  {t("modals.studentDetails.fields.studentName")}
                </span>
                <span className="text-gray-900 text-base font-semibold text-right">
                  {studentName}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-base font-normal">
                  {t("modals.studentDetails.fields.email")}
                </span>
                <span className="text-gray-900 text-base font-semibold text-right">
                  {student?.email || t("manageStudents.na")}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-base font-normal">
                  {t("modals.studentDetails.fields.grade")}
                </span>
                <span className="text-gray-900 text-base font-semibold text-right">
                  {student?.grade || t("manageStudents.na")}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-base font-normal">
                  {t("modals.studentDetails.fields.dateJoined")}
                </span>
                <span className="text-gray-900 text-base font-semibold text-right">
                  {formattedDate}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-base font-normal">
                  {t("modals.studentDetails.fields.totalAchievements")}
                </span>
                <span className="text-gray-900 text-base font-semibold text-right">
                  {student?.achievementCount || "0"}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-base font-normal">
                  {t("modals.studentDetails.fields.studentId")}
                </span>
                <span className="text-gray-900 text-sm font-semibold text-right font-mono">
                  {student?.id || student?._id || t("manageStudents.na")}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 text-base font-normal">
                      {t("modals.studentDetails.fields.status")}
                    </span>
                    <div
                      className={`px-2.5 py-1 rounded-[40px] flex items-center ${
                        isActive
                          ? "bg-emerald-100"
                          : "bg-red-100"
                      }`}
                    >
                      <span
                        className={`text-sm font-medium capitalize ${
                          isActive
                            ? "text-emerald-800"
                            : "text-red-800"
                        }`}
                      >
                        {student?.status || t("manageStudents.na")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="h-[1px] bg-gray-300"></div>

          <div className="flex justify-end items-center gap-2">
            {isActive && canManage && (
              <button
                onClick={() => onDepartStudent?.(student)}
                className="h-11 px-4 py-1.5 bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors"
              >
                <span className="text-white text-base font-medium leading-4">{t("modals.studentDetails.actions.departStudent")}</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="h-11 px-4 py-1.5 border border-blue-500 rounded-lg hover:bg-blue-50 transition-colors"
            >
              <span className="text-gray-900 text-base font-medium">
                {t("modals.common.close")}
              </span>
            </button>
            {isActive && canManage && (
              <button
                onClick={() => onToggleStatus(student)}
                className="h-11 px-4 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
              >
                <span className="text-white text-base font-medium leading-4">
                  {t("modals.studentDetails.actions.deactivateStudent")}
                </span>
              </button>
            )}
            {/* Activate functionality - commented out for future use
            {!isActive && (
              <button
                onClick={() => onToggleStatus(student)}
                className="h-11 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
              >
                <span className="text-white text-base font-medium leading-4">
                  Activate Student
                </span>
              </button>
            )}
            */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailStudentModal;
